import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

// Build-time gate. Cierge builds replace the entire RBT-laden component with
// a generic stub so Terser DCEs all RBT strings (people, partner names,
// programme copy).
const CiergeStub = () => (
    <div
        className="max-w-3xl mx-auto px-6 lg:px-10 py-24 text-slate-500"
        data-testid="tech-lab-unavailable"
    >
        This pathway is not available in this deployment.
    </div>
);

const STAGES = process.env.REACT_APP_BRAND_MODE === "cierge"
    ? []
    : [
        {
            n: "01",
            title: "Executive Awareness Session",
            duration: "60–90 minutes",
            body: "A leadership briefing with a live sandbox walkthrough. Frames responsible AI, cybersecurity and sustainability as a governance question, not a procurement one.",
            for: "Board, executive committee, sponsoring CxO.",
        },
        {
            n: "02",
            title: "Governed Scenario Workshop",
            duration: "Half-day or full-day",
            body: "Customer teams run a scenario, map their roles, identify authority gaps and review the evidence pack KATLAS produces. Output: a defensible operating picture.",
            for: "Executive team plus risk, cyber, data and operations leads.",
        },
        {
            n: "03",
            title: "Customer-Specific Sandbox Sprint",
            duration: "2–4 weeks",
            body: "We re-skin the sandbox to your operating context using synthetic data. The customer's real role taxonomy, real handoffs, real authority model — rehearsed before it goes live.",
            for: "Transformation leadership plus delegated functional owners.",
        },
        {
            n: "04",
            title: "Pilot Readiness Programme",
            duration: "6–12 weeks",
            body: "A governed pilot is defined end-to-end: workflow, authority model, AI risk review, cyber controls and evidence requirements. KATLAS receipts back every authorised action.",
            for: "Sponsor + accountable executive + KATLAS technical liaison.",
        },
    ];

function RbtTechLab() {
    return (
        <div data-testid="tech-lab-page">
            <section className="border-b border-slate-200 bg-slate-50/60">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-12">
                    <div className="overline mb-3">RBT customer pathway</div>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight max-w-4xl leading-[1.05]">
                        RBT Responsible{" "}
                        <span className="text-slate-500">Technology Lab.</span>
                    </h1>
                    <p className="mt-5 text-lg text-slate-700 max-w-3xl leading-relaxed">
                        From a single executive briefing through to a governed pilot.
                        Each stage uses this sandbox plus the KATLAS substrate beneath it.
                        RBT owns the customer relationship, education and facilitation.
                        KATLAS owns the operational governance proof.
                    </p>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    {STAGES.map((s) => (
                        <article
                            key={s.n}
                            className="tile rounded-sm p-7"
                            data-testid={`stage-${s.n}`}
                        >
                            <div className="flex items-baseline justify-between mb-3">
                                <span className="font-mono text-[11px] tracking-[0.22em] text-slate-500">
                                    STAGE {s.n}
                                </span>
                                <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-700">
                                    {s.duration}
                                </span>
                            </div>
                            <h2 className="text-2xl font-bold tracking-tight mb-3">
                                {s.title}
                            </h2>
                            <p className="text-sm text-slate-700 leading-relaxed mb-4">
                                {s.body}
                            </p>
                            <div className="pt-4 border-t border-slate-200">
                                <div className="overline mb-1">For</div>
                                <p className="text-sm text-slate-700">{s.for}</p>
                            </div>
                        </article>
                    ))}
                </div>
            </section>

            <section className="border-t border-slate-200 bg-slate-50/60">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-2 gap-10">
                    <div>
                        <div className="overline mb-3">Division of labour</div>
                        <h3 className="text-2xl font-bold tracking-tight mb-3">RBT</h3>
                        <ul className="space-y-2 text-sm text-slate-700 list-disc pl-5">
                            <li>Owns the customer relationship and sector adaptation.</li>
                            <li>Designs the executive education and facilitation.</li>
                            <li>Frames responsible technology leadership at board level.</li>
                            <li>Supports the customer team through pilot readiness.</li>
                        </ul>
                    </div>
                    <div>
                        <div className="overline mb-3">Division of labour</div>
                        <h3 className="text-2xl font-bold tracking-tight mb-3">KATLAS</h3>
                        <ul className="space-y-2 text-sm text-slate-700 list-disc pl-5">
                            <li>Operational governance substrate beneath every scenario.</li>
                            <li>CAR model: custody, authority, receipts at the point of action.</li>
                            <li>Signs and verifies receipts. Never holds raw private data.</li>
                            <li>Auditable evidence layer for regulators, insurers, customers.</li>
                        </ul>
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
                <div className="tile rounded-sm p-8 lg:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                        <div className="overline mb-2">Next step</div>
                        <h3 className="text-2xl font-bold tracking-tight">
                            Run a scenario before the conversation.
                        </h3>
                        <p className="text-sm text-slate-600 mt-2 max-w-xl">
                            Take 15 minutes inside the sandbox. Then talk to RBT about
                            what your operating model would need to change before live
                            deployment.
                        </p>
                    </div>
                    <Link
                        to="/"
                        className="btn-primary text-sm tracking-tight px-5 py-3 rounded-sm inline-flex items-center gap-2 self-start md:self-auto"
                        data-testid="tech-lab-cta-start"
                    >
                        Open the Lab <ArrowRight size={16} strokeWidth={1.5} />
                    </Link>
                </div>
            </section>
        </div>
    );
}

const TechLab = process.env.REACT_APP_BRAND_MODE === "cierge"
    ? CiergeStub
    : RbtTechLab;

export default TechLab;
